import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';

import { getBexApiContext } from 'bernie-context';
import { PageData } from 'bernie-http';

import { getSearchImpactingKeys, PropertySort, SearchCriteriaModel, SearchCriteriaUtils } from 'stores/models';

import { SearchResultsPageLayout } from '__generated__/api';

/**
 * This utility function is meant for store operations ONLY,
 * where a determination must be made about which network call to make.
 * All attempts should be made first to either log server-side or use <Experiment />.
 */
export function evaluateAndLogExperiment(
  experimentName: string,
  context?: ContextStore,
  experiments?: ExperimentStore,
  pageData?: PageData
): number {
  const experiment = experiments?.exposures?.[experimentName];

  if (experiment) {
    if (context && pageData) {
      experiments?.logExperiment(context, pageData, experiment);
    }

    return experiment.bucket;
  }

  return 0;
}

export function buildSRPVariables(context: ContextStore, destination: string): SearchResultsPageLayout.Variables {
  return {
    context: getBexApiContext(context),
    destination: { regionName: destination },
  };
}

function getComparableKeys(prevStore: SearchCriteriaModel, newStore: any) {
  let compareKeys = getSearchImpactingKeys();
  if (!didGeoChange(prevStore, newStore)) {
    compareKeys = compareKeys.filter(key => key !== 'regionId' && key !== 'latLong' && key !== 'destination');
  }

  // If we're asking to sort by values that's equivalent to what we currently have,
  // remove sort field from the comparison to avoid another service call
  const askingForEmptySortWhileWeHaveRecommended = newStore.sort === '' && prevStore.sort === PropertySort.RECOMMENDED;
  const askingForMctc2SortWhileWeHaveDeals = !newStore.sort && newStore.mctc === '2';
  const isSameSortParam = newStore.sort && prevStore.sort && newStore.sort.toLowerCase() === prevStore.sort.toLowerCase();
  const equivalentSort = askingForEmptySortWhileWeHaveRecommended || askingForMctc2SortWhileWeHaveDeals || isSameSortParam;

  if (equivalentSort) {
    compareKeys = compareKeys.filter(key => key !== 'sort');
  }

  return compareKeys;
}

function didGeoChange(prevStore: SearchCriteriaModel, newStore: any) {
  const { regionId: prevRegionId, latLong: prevLatLong, destination: prevDestination } = prevStore;
  const { regionId: newRegionId, latLong: newLatLong, destination: newDestination } = newStore;

  const isNewRegionIdEmpty = newRegionId === '';
  const isNewLatLongEmpty = newLatLong === '';
  const isNewDestinationEmpty = newDestination === '';
  const didRegionIdChange = !isNewRegionIdEmpty && newRegionId !== prevRegionId;
  const didLatLongChange = !isNewLatLongEmpty && newLatLong !== prevLatLong;
  const didDestinationChange = !isNewDestinationEmpty && newDestination !== prevDestination;

  return didRegionIdChange || (isNewRegionIdEmpty && (didLatLongChange || (isNewLatLongEmpty && didDestinationChange)));
}

export function hasStoresChanged(prevStore: SearchCriteriaModel, newStore: SearchCriteriaModel) {
  if (Boolean(newStore) && !prevStore) {
    return true;
  }

  return SearchCriteriaUtils.hasSearchCriteriaChanged(prevStore, newStore, getComparableKeys(prevStore, newStore));
}
